'use client';

import 'swiper/css';
import 'swiper/css/pagination';

import type { SwiperRef } from 'swiper/react';
import type { ReviewSliderProps } from '@/core/types';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import React, { useId, useRef, useState } from 'react';

import { Iconify } from '@/components/iconify';
import { Box, Image, Container, Typography } from '@/components/ui';

export default function ReviewSlider({
  title,
  subTitle,
  reviews = [],
  image,
  className = '',
}: ReviewSliderProps) {
  const clipPtahId = `review-clipped-${useId()}`;
  const swiperRef = useRef<SwiperRef>(null);

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const renderStars = (rating: number = 0) => (
    <div className="flex gap-3 justify-center">
      {[...Array(5)].map((_, index) => (
        <span
          key={index}
          className={`text-xl ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        >
          <Iconify icon="tabler:star-filled" width={20} />
        </span>
      ))}
    </div>
  );

  return (
    <div className={`${className} relative w-full py-16 md:py-24`}>
      {!!image?.url && (
        <Image
          src={image?.url || ''}
          alt={image?.alt || ''}
          aspectRatio="16/9"
          objectFit="fill"
          className="w-full absolute top-0 left-0 h-full object-cover -z-[1]"
          loading="eager"
        />
      )}

      <Container className="flex flex-col items-center justify-center text-center">
        <Box className="max-w-xl w-full mb-12">
          {title && (
            <Typography
              variant="h2"
              className="text-2xl text-center font-bold text-white mb-6 uppercase"
            >
              {title}
            </Typography>
          )}
          {subTitle && (
            <Typography
              variant="body1"
              paragraph
              className="text-white text-center text-lg md:text-xl mb-8"
            >
              {subTitle}
            </Typography>
          )}
        </Box>
      </Container>

      <Box className="w-full flex justify-center relative max-w-3xl m-auto md:px-6">
        <svg className="absolute h-0 w-0">
          <clipPath id={clipPtahId} clipPathUnits="objectBoundingBox">
            <path d="M1,0.01 C1,1,1,-0.284,1,1 C0.663,1,0.337,1,0,1 C0,-0.284,0,1,0,0 C0.126,0,0,0,0.392,0 C0.392,0.428,0.44,0.775,0.5,0.775 C0.559,0.775,0.607,0.434,0.608,0.01 C0.608,0.006,0.608,0.004,0.608,0 C1,0.01,0.874,0.01,1,0.01" />
          </clipPath>
        </svg>

        <div className="swiper-nav-buttons hidden md:block">
          <button
            disabled={isBeginning}
            className={`swiper-button-prev absolute cursor-pointer left-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-colors
              ${isBeginning ? 'text-white/70 cursor-not-allowed' : 'text-white/90 hover:text-white'}`}
            onClick={() => swiperRef.current?.swiper.slidePrev()}
          >
            <Iconify icon="lucide:chevron-left" width={42} />
          </button>
          <button
            disabled={isEnd}
            className={`swiper-button-next cursor-pointer absolute right-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-colors
              ${isEnd ? 'text-white/70 cursor-not-allowed' : 'text-white/90 hover:text-white'}`}
            onClick={() => swiperRef.current?.swiper.slideNext()}
          >
            <Iconify icon="lucide:chevron-right" width={42} />
          </button>
        </div>

        <Container className="overflow-hidden">
          <Swiper
            ref={swiperRef}
            modules={[Navigation, Autoplay]}
            spaceBetween={24}
            slidesPerView={1.1}
            onSwiper={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1260: { slidesPerView: 3 },
            }}
            navigation={false}
            className="review-slider justify-center !overflow-visible lg:!overflow-hidden"
          >
            {reviews.map((review) => (
              <SwiperSlide key={review.id} className="!h-auto">
                <Box className="relative rounded-b-[32px] overflow-hidden h-full text-center pt-[32px]">
                  {review.avatar && (
                    <>
                      <div
                        className="flex w-full justify-center items-center rounded-t-[32px] -mb-[12px] relative overflow-hidden z-[-1]
                          before:content-[''] before:absolute before:bg-white before:-left-1/2 before:h-full before:top-[1px] before:right-2/3 before:z-[-1]
                          after:content-[''] after:absolute after:-right-1/2 after:h-full after:top-[1px] after:left-2/3 after:bg-white after:z-[-2]"
                      >
                        <div
                          className="h-[56px] min-w-[400px] w-[400px] bg-white"
                          style={{ clipPath: `url(#${clipPtahId})` }}
                        />
                      </div>
                      <Image
                        src={review.avatar}
                        alt={review.name}
                        className="absolute top-0 left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full mx-auto object-cover bg-white"
                        width={64}
                        height={64}
                        objectFit="cover"
                        loading="lazy"
                      />
                    </>
                  )}
                  <Box className="text-center h-full w-full bg-white shadow-md overflow-hidden space-y-1 p-6 pt-4 max-w-md mx-auto z-10">
                    <Typography
                      variant="h4"
                      className="text-center text-sm font-semibold text-black mb-3"
                    >
                      {review.name}
                    </Typography>
                    <Box className="flex justify-center items-center gap-1 text-yellow-400 mb-4">
                      {review.rating && renderStars(review.rating)}
                    </Box>
                    <Typography variant="body1" paragraph className="text-sm text-center">
                      {review.content}
                    </Typography>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Container>
      </Box>
    </div>
  );
}
